import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, Funnel, Buildings, MapPin, Star, ArrowRight, Sun, House, Image, Lightning, Trash, Warning, Pause, Play } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import CollapsibleSection from '../components/CollapsibleSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  municipal: 'Municipal', private: 'Private', sports_complex: 'Sports Complex',
  aquatic_center: 'Aquatic Center', swim_club: 'Swim Club', school: 'School',
  wellness: 'Wellness', hotel_pool: 'Hotel Pool'
};

const POOL_CAT_LABELS = { indoor: 'Overdekt', outdoor: 'Openlucht', unknown: 'Onbekend' };
const POOL_CAT_COLORS = {
  indoor: 'bg-sky-100 text-sky-800',
  outdoor: 'bg-amber-100 text-amber-800',
  unknown: 'bg-zinc-100 text-zinc-500',
};

const TYPE_COLORS = {
  municipal: 'bg-blue-100 text-blue-800', private: 'bg-purple-100 text-purple-800',
  sports_complex: 'bg-teal-100 text-teal-800', aquatic_center: 'bg-cyan-100 text-cyan-800',
  swim_club: 'bg-indigo-100 text-indigo-800', school: 'bg-orange-100 text-orange-800',
  wellness: 'bg-pink-100 text-pink-800', hotel_pool: 'bg-rose-100 text-rose-800'
};

function LeadScoreBadge({ score }) {
  const color = score >= 70 ? 'score-high' : score >= 40 ? 'score-medium' : 'score-low';
  return <span className={`font-data text-sm font-bold ${color}`}>{score}</span>;
}

export default function FacilitiesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [facilities, setFacilities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    country: searchParams.get('country') || '',
    facility_type: '',
    pool_category: '',
    verified_only: false,
    has_email: false,
    has_technical_contact: false,
    exportable_only: false,
    municipal_only: false,
    min_lead_score: '',
  });
  const [skip, setSkip] = useState(0);
  const [enrichingId, setEnrichingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [activeProfileSlug, setActiveProfileSlug] = useState('');
  const [filterConfig, setFilterConfig] = useState({ facility_dimensions: [], uses_default_facility_filters: true });
  const [dimValues, setDimValues] = useState({}); // {key: value}
  const [classifyStats, setClassifyStats] = useState(null);
  const [classifyJob, setClassifyJob] = useState(null);
  const [dedupeStats, setDedupeStats] = useState(null);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [relevanceStats, setRelevanceStats] = useState(null);
  const [relevanceJob, setRelevanceJob] = useState(null);
  const [relevanceFilter, setRelevanceFilter] = useState('default');  // default | on_topic | review | off_topic | unscored | all
  const [decisionBusy, setDecisionBusy] = useState(null);

  const loadDedupeStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/facilities/dedupe-emails/stats`, { credentials: 'include' });
      if (r.ok) setDedupeStats(await r.json());
    } catch { /* silent */ }
  }, []);

  const runDedupe = async () => {
    if (!dedupeStats) return;
    const msg = `Verwijder ${dedupeStats.contacts_to_delete} dubbele contacten en schoon ${dedupeStats.facilities_with_email_dupes} facility-e-mail-arrays op? Dit is een permanente actie.`;
    if (!window.confirm(msg)) return;
    setDedupeRunning(true);
    try {
      const r = await fetch(`${API}/facilities/dedupe-emails`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Ontdubbeling mislukt');
      window.alert(
        `✓ Ontdubbeling klaar:\n` +
        `· ${d.contacts_deleted} dubbele contacten verwijderd\n` +
        `· ${d.duplicate_contact_groups_processed} groepen verwerkt (per facility)\n` +
        `· ${d.profile_wide_email_groups_merged || 0} extra groepen gemerged op profiel-niveau\n` +
        `· ${d.contact_emails_lowercased} e-mails genormaliseerd naar kleine letters\n` +
        `· ${d.facilities_cleaned} facility-records opgeschoond`
      );
      await Promise.all([loadDedupeStats(), fetchFacilities()]);
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setDedupeRunning(false);
    }
  };

  const loadClassifyStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/facilities/classify-dimensions/stats`, { credentials: 'include' });
      if (r.ok) setClassifyStats(await r.json());
    } catch { /* silent */ }
  }, []);

  const loadRelevanceStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/facilities/relevance/stats`, { credentials: 'include' });
      if (r.ok) setRelevanceStats(await r.json());
    } catch { /* silent */ }
  }, []);

  const startRelevanceAudit = async () => {
    if (!relevanceStats || !relevanceStats.has_context) {
      window.alert('Stel eerst een marktbeschrijving of productcontext in via Profiel-instellingen.');
      return;
    }
    const left = relevanceStats.unscored || 0;
    if (left === 0 && !window.confirm('Alle facilities zijn al gescoord. Opnieuw scoren (force)?')) {
      return;
    }
    if (left > 0 && !window.confirm(`Scoor ${Math.min(left, 200)} facilities met GPT-5.2 op relevantie? Off-topic leads (< 0.30) worden automatisch verborgen.`)) {
      return;
    }
    const force = left === 0 ? '&force=true' : '';
    try {
      const r = await fetch(`${API}/facilities/relevance/audit?limit=200${force}`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Start mislukt');
      setRelevanceJob({ ...d, processed: 0, total: 0, scored: 0 });
      const poll = setInterval(async () => {
        try {
          const r2 = await fetch(`${API}/facilities/relevance/status/${d.job_id}`, { credentials: 'include' });
          const j = await r2.json();
          setRelevanceJob(j);
          if (j.status === 'done' || j.status === 'error') {
            clearInterval(poll);
            loadRelevanceStats();
            fetchFacilities();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      window.alert('Fout: ' + e.message);
    }
  };

  const decideRelevance = async (facilityId, decision) => {
    setDecisionBusy(facilityId);
    try {
      const r = await fetch(`${API}/facilities/${facilityId}/relevance-decision`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Mislukt');
      await Promise.all([loadRelevanceStats(), fetchFacilities()]);
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setDecisionBusy(null);
    }
  };

  const startClassifyJob = async () => {
    if (!classifyStats || classifyStats.unclassified === 0) return;
    if (!window.confirm(`Klassificeer ${Math.min(classifyStats.unclassified, 200)} facilities met GPT-5.2 op de profile-dimensies (${(classifyStats.dimension_keys || []).join(', ')})? Dit kost ongeveer 1 token-call per facility.`)) return;
    try {
      const r = await fetch(`${API}/facilities/classify-dimensions?limit=200`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Start mislukt');
      setClassifyJob({ ...d, processed: 0, total: 0, classified: 0, failed: 0 });
      // Poll
      const poll = setInterval(async () => {
        try {
          const r2 = await fetch(`${API}/facilities/classify-dimensions/status/${d.job_id}`, { credentials: 'include' });
          const j = await r2.json();
          setClassifyJob(j);
          if (j.status === 'done' || j.status === 'error') {
            clearInterval(poll);
            loadClassifyStats();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      alert(e.message);
    }
  };

  // Load active profile slug for purge confirm
  useEffect(() => {
    fetch(`${API}/profiles`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setActiveProfileSlug(d.active || ''))
      .catch(() => {});
    // Load dynamic filter config for active profile
    fetch(`${API}/profiles/filter-config`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setFilterConfig(d); setDimValues({}); })
      .catch(() => {});
    loadClassifyStats();
    loadDedupeStats();
    loadRelevanceStats();
  }, [loadClassifyStats, loadDedupeStats, loadRelevanceStats]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === facilities.length && facilities.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(facilities.map(f => f.facility_id)));
    }
  };

  const deleteOne = async (facilityId, name) => {
    if (!window.confirm(`Facility "${name}" en alle bijbehorende contacten verwijderen?`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/facilities/${facilityId}`, { method: 'DELETE', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verwijderen mislukt');
      setSelected(prev => { const n = new Set(prev); n.delete(facilityId); return n; });
      await fetchFacilities();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} geselecteerde facilities + bijbehorende contacten verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/facilities/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility_ids: ids, confirm: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verwijderen mislukt');
      window.alert(`✓ ${d.facilities_deleted} facilities + ${d.contacts_deleted} contacten verwijderd.`);
      setSelected(new Set());
      await fetchFacilities();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  const purgeProfile = async () => {
    if (purgeConfirm !== activeProfileSlug) {
      window.alert(`Typ de profiel-slug "${activeProfileSlug}" exact om dit uit te voeren.`);
      return;
    }
    setDeleting(true);
    try {
      const r = await fetch(`${API}/profile/purge-data`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_text: purgeConfirm }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Purge mislukt');
      window.alert(
        `✓ Profiel "${d.profile}" leeggemaakt:\n` +
        `· Facilities: ${d.facilities_deleted}\n` +
        `· Contacten: ${d.contacts_deleted}\n` +
        `· Bron-records: ${d.source_records_deleted}\n` +
        `· Scan jobs: ${d.scan_jobs_deleted}`
      );
      setShowPurge(false);
      setPurgeConfirm('');
      setSelected(new Set());
      await fetchFacilities();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  const quickEnrich = async (facilityId) => {
    setEnrichingId(facilityId);
    try {
      await fetch(`${API}/enrich/facility/${facilityId}`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    setTimeout(() => { setEnrichingId(null); fetchFacilities(); }, 3000);
  };

  // Toggle marketing-pauze: pas eerst optimistisch lokaal toe, daarna persist.
  // Bij faal terugdraaien zodat UI altijd consistent is met DB.
  const toggleMarketingPause = async (facility) => {
    const wasPaused = !!facility.marketing_paused;
    const willPause = !wasPaused;
    let reason = null;
    if (willPause) {
      const ans = window.prompt(
        `Pauzeer marketing voor "${facility.name}"?\n\nDeze faciliteit wordt uitgesloten uit ALLE actieve campagnes (sequences + broadcasts) tot je 'm weer aanzet.\n\nReden (optioneel):`,
        ''
      );
      if (ans === null) return; // cancelled
      reason = ans.trim() || null;
    }
    // Optimistic UI
    setFacilities(prev => prev.map(f =>
      f.facility_id === facility.facility_id
        ? { ...f, marketing_paused: willPause, marketing_pause_reason: reason }
        : f
    ));
    try {
      const r = await fetch(`${API}/facilities/${facility.facility_id}/marketing-pause`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: willPause, reason }),
      });
      if (!r.ok) throw new Error('toggle-failed');
    } catch {
      // Rollback
      setFacilities(prev => prev.map(f =>
        f.facility_id === facility.facility_id
          ? { ...f, marketing_paused: wasPaused }
          : f
      ));
      window.alert('Pauze-status kon niet worden opgeslagen.');
    }
  };

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.country) params.set('country', filters.country);
    if (filters.verified_only) params.set('verified_only', 'true');
    if (filters.has_email) params.set('has_email', 'true');
    if (filters.has_technical_contact) params.set('has_technical_contact', 'true');
    if (filters.exportable_only) params.set('exportable_only', 'true');
    if (filters.municipal_only) params.set('municipal_only', 'true');
    if (filters.min_lead_score) params.set('min_lead_score', filters.min_lead_score);
    if (search) params.set('search', search);
    if (relevanceFilter && relevanceFilter !== 'default') params.set('relevance', relevanceFilter);
    // Apply dimension filters (either as native top-level params for default
    // pool-industry filters, or as dim_{key} for AI-generated profile dims).
    const nativeKeys = new Set(['country', 'facility_type', 'pool_category', 'min_lead_score']);
    Object.entries(dimValues).forEach(([k, v]) => {
      if (!v) return;
      if (nativeKeys.has(k)) params.set(k, v); else params.set(`dim_${k}`, v);
    });
    params.set('skip', skip.toString());
    params.set('limit', '50');
    try {
      const res = await fetch(`${API}/facilities?${params}`, { credentials: 'include' });
      const data = await res.json();
      setFacilities(data.facilities || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [filters, search, skip, dimValues, relevanceFilter]);

  useEffect(() => { fetchFacilities(); }, [fetchFacilities]);

  const updateFilter = (key, value) => {
    setSkip(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="animate-fade-in" data-testid="facilities-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Facilities</h1>
          <p className="text-sm text-zinc-500 mt-1">
            <span className="font-data font-bold">{total}</span> {filterConfig.uses_default_facility_filters ? 'swimming pools' : 'records'} gevonden
            {selected.size > 0 && <span className="ml-2 text-zinc-700">· {selected.size} geselecteerd</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dedupeStats && (dedupeStats.contacts_to_delete > 0 || dedupeStats.facilities_with_email_dupes > 0) && (
            <button
              data-testid="dedupe-emails-btn"
              onClick={runDedupe}
              disabled={dedupeRunning}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:opacity-50 transition-colors"
              title="Verwijder dubbele e-mailadressen (case-insensitive) en normaliseer e-mails naar kleine letters. Wordt ook automatisch uitgevoerd na elke scan."
            >
              <Trash size={14} weight="bold" />
              {dedupeRunning
                ? 'Ontdubbelen...'
                : `Ontdubbel ${dedupeStats.contacts_to_delete} mails`}
            </button>
          )}
          {classifyStats && classifyStats.configured_dimensions > 0 && classifyStats.unclassified > 0 && (
            <button
              data-testid="classify-dimensions-btn"
              onClick={startClassifyJob}
              disabled={classifyJob?.status === 'running'}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              title={`AI-classificatie op ${(classifyStats.dimension_keys || []).join(', ')}`}
            >
              <Lightning size={14} weight="bold" />
              {classifyJob?.status === 'running'
                ? `Classificeren... ${classifyJob.processed || 0}/${classifyJob.total || 0}`
                : `Classificeer ${classifyStats.unclassified} ontbrekende`}
            </button>
          )}
          {relevanceStats && relevanceStats.has_context && (
            <button
              data-testid="relevance-audit-btn"
              onClick={startRelevanceAudit}
              disabled={relevanceJob?.status === 'running'}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-fuchsia-600 text-white rounded-sm hover:bg-fuchsia-700 disabled:opacity-50 transition-colors"
              title="AI-relevantie scoren tegen profielcontext. Off-topic facilities (< 0.30) worden automatisch verborgen."
            >
              <Warning size={14} weight="bold" />
              {relevanceJob?.status === 'running'
                ? `Scoren... ${relevanceJob.processed || 0}/${relevanceJob.total || 0}`
                : (relevanceStats.unscored > 0
                    ? `Relevantie audit (${relevanceStats.unscored})`
                    : 'Relevantie audit')}
            </button>
          )}
          {selected.size > 0 && (
            <button
              data-testid="bulk-delete-facilities-btn"
              onClick={deleteSelected}
              disabled={deleting}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <Trash size={14} weight="bold" /> Verwijder {selected.size}
            </button>
          )}
          <button
            data-testid="open-purge-profile-btn"
            onClick={() => setShowPurge(s => !s)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-red-300 text-red-700 rounded-sm hover:bg-red-50 transition-colors"
            title="Wis alle facilities, contacten en scan jobs van dit profiel"
          >
            <Warning size={14} weight="bold" /> Profiel leegmaken
          </button>
        </div>
      </div>

      {/* Relevance filter pills — alleen tonen wanneer er gescoorde data is */}
      {relevanceStats && relevanceStats.has_context && (relevanceStats.total - relevanceStats.unscored) > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="relevance-filter-pills">
          <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Relevantie:</span>
          {[
            { key: 'default', label: `On-topic + review`, count: relevanceStats.on_topic + relevanceStats.review, color: 'bg-zinc-900 text-white' },
            { key: 'on_topic', label: 'On-topic', count: relevanceStats.on_topic, color: 'bg-emerald-100 text-emerald-900 border border-emerald-300' },
            { key: 'review', label: 'Review', count: relevanceStats.review, color: 'bg-amber-100 text-amber-900 border border-amber-300' },
            { key: 'off_topic', label: 'Off-topic', count: relevanceStats.off_topic, color: 'bg-red-100 text-red-900 border border-red-300' },
            { key: 'unscored', label: 'Nog niet gescoord', count: relevanceStats.unscored, color: 'bg-zinc-100 text-zinc-700 border border-zinc-300' },
            { key: 'all', label: 'Alles tonen', count: relevanceStats.total, color: 'bg-white text-zinc-700 border border-zinc-300' },
          ].map(p => {
            const active = relevanceFilter === p.key;
            return (
              <button
                key={p.key}
                data-testid={`relevance-pill-${p.key}`}
                onClick={() => { setRelevanceFilter(p.key); setSkip(0); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                  active ? p.color + ' ring-2 ring-offset-1 ring-zinc-400' : p.color + ' opacity-70 hover:opacity-100'
                }`}
              >
                {p.label} <span className="ml-1 font-mono font-bold">{p.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {showPurge && (
        <div className="mb-4 border border-red-300 bg-red-50 rounded-sm p-4" data-testid="purge-profile-card">
          <div className="flex items-start gap-3 mb-3">
            <Warning size={20} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <strong>Profiel "{activeProfileSlug}" volledig leegmaken</strong>
              <div className="mt-1 text-xs">
                Dit verwijdert ALLE facilities, contacten, bron-records én scan jobs van dit profiel.
                Profiel-instellingen, zoektermen, e-mails en kennisbank blijven behouden.
                Typ de profielnaam <code className="bg-red-100 px-1 rounded">{activeProfileSlug}</code> om te bevestigen.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={purgeConfirm}
              onChange={e => setPurgeConfirm(e.target.value)}
              placeholder={`Typ "${activeProfileSlug}"`}
              className="flex-1 px-3 py-2 text-sm border border-red-300 rounded-sm bg-white focus:outline-none focus:border-red-500"
              data-testid="purge-confirm-input"
            />
            <button
              onClick={purgeProfile}
              disabled={deleting || purgeConfirm !== activeProfileSlug}
              className="px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-40"
              data-testid="purge-confirm-btn"
            >
              Definitief leegmaken
            </button>
            <button
              onClick={() => { setShowPurge(false); setPurgeConfirm(''); }}
              className="px-3 py-2 text-sm border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50"
            >
              Annuleer
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <CollapsibleSection
        id="facilities-filters"
        title={`Filters${(search || filters.country || filters.exportable_only || filters.verified_only || Object.keys(filters.dimensions || {}).some(k => filters.dimensions[k])) ? ' (actief)' : ''}`}
        icon={Funnel}
        accent={(search || filters.country || filters.exportable_only || filters.verified_only || Object.keys(filters.dimensions || {}).some(k => filters.dimensions[k])) ? 'text-[#0055FF]' : 'text-zinc-500'}
        storageKey="facilities:collapsedSections"
      >
        <div className="border border-zinc-200 bg-white p-4" data-testid="facilities-filters">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="relative col-span-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="facility-search-input"
              placeholder="Search facilities..."
              value={search}
              onChange={e => { setSearch(e.target.value); setSkip(0); }}
              className="pl-9 rounded-sm text-sm"
            />
          </div>
          <Select value={filters.country} onValueChange={v => updateFilter('country', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="country-filter" className="rounded-sm text-sm">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="NL">Netherlands</SelectItem>
              <SelectItem value="BE">Belgium</SelectItem>
              <SelectItem value="FR">France</SelectItem>
              <SelectItem value="DE">Germany</SelectItem>
            </SelectContent>
          </Select>
          {/* Dynamic per-profile filter dimensions */}
          {(filterConfig.facility_dimensions || []).map(dim => (
            <Select
              key={dim.key}
              value={dimValues[dim.key] || ''}
              onValueChange={v => setDimValues(prev => ({ ...prev, [dim.key]: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid={`dim-filter-${dim.key}`} className="rounded-sm text-sm">
                <SelectValue placeholder={dim.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{dim.label} — Alle</SelectItem>
                {(dim.options || []).map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <Select value={filters.min_lead_score} onValueChange={v => updateFilter('min_lead_score', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="score-filter" className="rounded-sm text-sm">
              <SelectValue placeholder="Min Score" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Score</SelectItem>
              <SelectItem value="80">80+</SelectItem>
              <SelectItem value="60">60+</SelectItem>
              <SelectItem value="40">40+</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-4 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={filters.verified_only} onChange={e => updateFilter('verified_only', e.target.checked)} className="rounded-sm" />
              Verified
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={filters.exportable_only} onChange={e => updateFilter('exportable_only', e.target.checked)} className="rounded-sm" />
              Exportable
            </label>
          </div>
        </div>
        </div>
      </CollapsibleSection>

      {/* Results Table */}
      <div className="border border-zinc-200 overflow-hidden" data-testid="facilities-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === facilities.length}
                  onChange={toggleSelectAll}
                  className="rounded-sm cursor-pointer"
                  data-testid="select-all-facilities"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Facility</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Location</th>
              {filterConfig.uses_default_facility_filters && (
                <>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Pool</th>
                </>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Score</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={filterConfig.uses_default_facility_filters ? 8 : 6} className="px-4 py-12 text-center text-zinc-400">Loading...</td></tr>
            ) : facilities.length === 0 ? (
              <tr><td colSpan={filterConfig.uses_default_facility_filters ? 8 : 6} className="px-4 py-12 text-center text-zinc-400">No facilities found</td></tr>
            ) : (
              facilities.map(f => (
                <tr
                  key={f.facility_id}
                  data-testid={`facility-row-${f.facility_id}`}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors duration-150 ${selected.has(f.facility_id) ? 'bg-blue-50/40' : ''} ${f.marketing_paused ? 'opacity-60' : ''}`}
                  onClick={() => navigate(`/facilities/${f.facility_id}`)}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(f.facility_id)}
                      onChange={() => toggleSelect(f.facility_id)}
                      className="rounded-sm cursor-pointer"
                      data-testid={`select-facility-${f.facility_id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {f.images && f.images.length > 0 ? (
                        <img src={f.images[0]} alt="" className="w-10 h-10 rounded-sm object-cover border border-zinc-200 shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-sm bg-zinc-100 flex items-center justify-center shrink-0">
                          <Buildings size={16} className="text-zinc-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {f.name}
                          {f.marketing_paused && (
                            <span
                              title={f.marketing_pause_reason ? `Marketing-pauze: ${f.marketing_pause_reason}` : 'Marketing-pauze actief'}
                              className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm flex items-center gap-1"
                              data-testid={`marketing-paused-badge-${f.facility_id}`}
                            >
                              <Pause size={8} weight="fill" /> Pauze
                            </span>
                          )}
                          {f.relevance_status && f.relevance_status !== 'on_topic' && (
                            <span
                              title={f.relevance_reason || ''}
                              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                                f.relevance_status === 'off_topic'
                                  ? 'bg-red-100 text-red-800 border-red-300'
                                  : 'bg-amber-100 text-amber-800 border-amber-300'
                              }`}
                              data-testid={`relevance-badge-${f.facility_id}`}
                            >
                              {f.relevance_status === 'off_topic' ? 'Off-topic' : 'Review'}{typeof f.relevance_score === 'number' ? ` · ${f.relevance_score.toFixed(2)}` : ''}
                            </span>
                          )}
                        </div>
                        {f.alt_name && <div className="text-xs text-zinc-400">{f.alt_name}</div>}
                        {f.relevance_status && f.relevance_status !== 'on_topic' && f.relevance_reason && (
                          <div className="text-[11px] text-zinc-500 mt-0.5 italic max-w-md truncate" title={f.relevance_reason}>
                            {f.relevance_reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-zinc-400" />
                      <span>{f.city}, {f.country_code}</span>
                    </div>
                    <div className="text-xs text-zinc-400">{f.region}</div>
                  </td>
                  {filterConfig.uses_default_facility_filters && (
                    <>
                      <td className="px-4 py-3">
                        <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${TYPE_COLORS[f.facility_type] || 'bg-zinc-100 text-zinc-800'}`}>
                          {TYPE_LABELS[f.facility_type] || f.facility_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {f.pool_category === 'indoor' ? <House size={13} className="text-sky-600" /> :
                           f.pool_category === 'outdoor' ? <Sun size={13} className="text-amber-600" /> : null}
                          <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${POOL_CAT_COLORS[f.pool_category] || POOL_CAT_COLORS.unknown}`}>
                            {POOL_CAT_LABELS[f.pool_category] || 'Onbekend'}
                          </Badge>
                        </div>
                    {f.images && f.images.length > 0 && (
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <Image size={10} className="text-zinc-400" />
                        <span className="text-[10px] text-zinc-400">{f.images.length}</span>
                      </div>
                    )}
                  </td>
                    </>
                  )}
                  <td className="px-4 py-3"><LeadScoreBadge score={f.lead_score} /></td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${f.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {f.verification_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right flex items-center gap-2 justify-end">
                    <button
                      data-testid={`marketing-pause-${f.facility_id}`}
                      onClick={(e) => { e.stopPropagation(); toggleMarketingPause(f); }}
                      className={`p-1.5 rounded-sm transition-colors ${
                        f.marketing_paused
                          ? 'text-emerald-600 hover:bg-emerald-50'
                          : 'text-amber-600 hover:bg-amber-50'
                      }`}
                      title={f.marketing_paused
                        ? 'Marketing-pauze opheffen — facility weer in campagnes'
                        : 'Pauzeer marketing — facility uitsluiten van campagnes'}
                    >
                      {f.marketing_paused ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
                    </button>
                    <button
                      data-testid={`quick-enrich-${f.facility_id}`}
                      onClick={(e) => { e.stopPropagation(); quickEnrich(f.facility_id); }}
                      className="px-2 py-1 text-[10px] font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] transition-colors flex items-center gap-1"
                      title="Quick Enrich"
                    >
                      <Lightning size={10} weight="bold" /> Enrich
                    </button>
                    {f.relevance_status && f.relevance_status !== 'on_topic' && (
                      <button
                        data-testid={`relevance-keep-${f.facility_id}`}
                        onClick={(e) => { e.stopPropagation(); decideRelevance(f.facility_id, 'keep'); }}
                        disabled={decisionBusy === f.facility_id}
                        className="px-2 py-1 text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-sm hover:bg-emerald-200 transition-colors disabled:opacity-50"
                        title="Markeer als relevant — wordt weer zichtbaar in normale lijst"
                      >
                        Behoud
                      </button>
                    )}
                    {f.relevance_status !== 'off_topic' && (
                      <button
                        data-testid={`relevance-archive-${f.facility_id}`}
                        onClick={(e) => { e.stopPropagation(); decideRelevance(f.facility_id, 'archive'); }}
                        disabled={decisionBusy === f.facility_id}
                        className="px-2 py-1 text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 rounded-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                        title="Archiveer als off-topic — verberg uit normale lijst"
                      >
                        Off-topic
                      </button>
                    )}
                    <button
                      data-testid={`delete-facility-${f.facility_id}`}
                      onClick={(e) => { e.stopPropagation(); deleteOne(f.facility_id, f.name); }}
                      disabled={deleting}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-sm transition-colors disabled:opacity-40"
                      title="Verwijder"
                    >
                      <Trash size={14} />
                    </button>
                    <ArrowRight size={16} className="text-zinc-400" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="facilities-pagination">
          <span className="text-zinc-500">
            Showing {skip + 1}-{Math.min(skip + 50, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - 50))}
              className="px-3 py-1.5 border border-zinc-200 rounded-sm disabled:opacity-50 hover:bg-zinc-50"
            >
              Previous
            </button>
            <button
              disabled={skip + 50 >= total}
              onClick={() => setSkip(skip + 50)}
              className="px-3 py-1.5 border border-zinc-200 rounded-sm disabled:opacity-50 hover:bg-zinc-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
